// Firebase Firestore service for goals management
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { canEdit } from './calendarStorage';

const GOALS_COLLECTION = 'goals';
const TASKS_COLLECTION = 'tasks';

/**
 * Serialize a Firestore goal document to a plain JS object.
 * Converts all known Timestamp fields to ISO strings and fills
 * Last 10% fields with safe defaults when absent.
 *
 * @param {string} id  - Firestore doc ID
 * @param {Object} data - Raw Firestore data
 * @returns {Object}
 */
const serializeGoalDoc = (id, data) => ({
    id,
    ...data,
    // Standard timestamps
    createdAt: data.createdAt?.toDate().toISOString() ?? null,
    updatedAt: data.updatedAt?.toDate().toISOString() ?? null,
    targetDate: data.targetDate?.toDate().toISOString() ?? null,
    // Last 10% timestamps
    last10EnteredAt: data.last10EnteredAt?.toDate().toISOString() ?? null,
    finishModeActivatedAt: data.finishModeActivatedAt?.toDate().toISOString() ?? null,
    timestampLastActivity: data.timestampLastActivity?.toDate().toISOString() ?? null,
});

/**
 * Get all goals for a calendar
 * @param {string} calendarId - The calendar ID
 * @param {string} userId - The user's ID (for permission check)
 * @returns {Promise<Array>} Array of goal objects
 */
export const getGoals = async (calendarId, userId) => {
    if (!userId || !calendarId) {
        throw new Error('User ID and Calendar ID are required');
    }

    try {
        const goalsRef = collection(db, GOALS_COLLECTION);
        const q = query(
            goalsRef,
            where('calendarId', '==', calendarId)
        );

        const querySnapshot = await getDocs(q);
        const goals = [];

        querySnapshot.forEach((docSnap) => {
            goals.push(serializeGoalDoc(docSnap.id, docSnap.data()));
        });

        // Sort by createdAt (newest first)
        goals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        return goals;
    } catch (error) {
        console.error('Error getting goals from Firestore:', error);
        throw error;
    }
};

/**
 * Get goals filtered by time period
 * @param {string} calendarId - The calendar ID
 * @param {string} period - 'yearly', 'quarterly', or 'monthly'
 * @param {number} year - The year
 * @param {number} quarter - Quarter (1-4, required for quarterly)
 * @param {number} month - Month (1-12, required for monthly)
 * @returns {Promise<Array>} Array of filtered goal objects
 */
export const getGoalsByPeriod = async (calendarId, period, year, quarter = null, month = null) => {
    if (!calendarId || !period || !year) {
        throw new Error('Calendar ID, period, and year are required');
    }

    try {
        const goalsRef = collection(db, GOALS_COLLECTION);
        let q = query(
            goalsRef,
            where('calendarId', '==', calendarId),
            where('period', '==', period),
            where('year', '==', year)
        );

        // Add additional filters based on period
        if (period === 'quarterly' && quarter) {
            q = query(
                goalsRef,
                where('calendarId', '==', calendarId),
                where('period', '==', period),
                where('year', '==', year),
                where('quarter', '==', quarter)
            );
        } else if (period === 'monthly' && month) {
            q = query(
                goalsRef,
                where('calendarId', '==', calendarId),
                where('period', '==', period),
                where('year', '==', year),
                where('month', '==', month)
            );
        }

        const querySnapshot = await getDocs(q);
        const goals = [];

        querySnapshot.forEach((docSnap) => {
            goals.push(serializeGoalDoc(docSnap.id, docSnap.data()));
        });

        return goals;
    } catch (error) {
        console.error('Error getting goals by period:', error);
        throw error;
    }
};

/**
 * Get a single goal by ID
 * @param {string} goalId - The goal ID
 * @param {string} userId - The user's ID (for permission check)
 * @returns {Promise<Object>} Goal object
 */
export const getGoalById = async (goalId, userId) => {
    if (!userId || !goalId) {
        throw new Error('User ID and Goal ID are required');
    }

    try {
        const goalRef = doc(db, GOALS_COLLECTION, goalId);
        const docSnap = await getDoc(goalRef);

        if (!docSnap.exists()) {
            throw new Error('Goal not found');
        }

        return serializeGoalDoc(docSnap.id, docSnap.data());
    } catch (error) {
        console.error('Error getting goal by ID:', error);
        throw error;
    }
};

/**
 * Create a new goal
 * @param {Object} goalData - Goal data
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} Created goal object
 */
export const createGoal = async (goalData, userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    if (!goalData.calendarId) {
        throw new Error('Calendar ID is required');
    }

    // Check permissions - only owner and editor can create goals
    const hasPermission = await canEdit(goalData.calendarId, userId);
    if (!hasPermission) {
        throw new Error('Insufficient permissions to create goals');
    }

    try {
        const goalsRef = collection(db, GOALS_COLLECTION);

        // Calculate target date based on period
        let targetDate = new Date(goalData.year, 0, 1); // Default to start of year

        if (goalData.period === 'yearly') {
            // December 31st at 23:59:59
            targetDate = new Date(goalData.year, 11, 31, 23, 59, 59, 999);
        } else if (goalData.period === 'quarterly' && goalData.quarter) {
            const quarterEndMonth = goalData.quarter * 3; // 3, 6, 9, 12
            // Last day of quarter at 23:59:59
            targetDate = new Date(goalData.year, quarterEndMonth, 0, 23, 59, 59, 999);
        } else if (goalData.period === 'monthly' && goalData.month) {
            // Last day of month at 23:59:59
            targetDate = new Date(goalData.year, goalData.month, 0, 23, 59, 59, 999);
        }

        const newGoal = {
            calendarId: goalData.calendarId,
            title: goalData.title,
            description: goalData.description || '',
            period: goalData.period,
            year: goalData.year,
            quarter: goalData.quarter || null,
            month: goalData.month || null,
            targetDate: targetDate,
            color: goalData.color || 'blue',
            status: 'active',
            createdBy: userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            // ── Last 10% fields (default state) ──────────────────────────
            last10EnteredAt: null,
            finishModeActivated: false,
            finishModeActivatedAt: null,
            completedFromLast10: false,
            abandonedInLast10: false,
            timestampLastActivity: null,
            daysStalled: 0,
            percentageAtLast10Entry: null
        };

        const docRef = await addDoc(goalsRef, newGoal);

        return {
            id: docRef.id,
            ...newGoal,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            targetDate: targetDate.toISOString(),
            // Timestamps default to null on creation (serverTimestamp() is opaque locally)
            last10EnteredAt: null,
            finishModeActivatedAt: null,
            timestampLastActivity: null
        };
    } catch (error) {
        console.error('Error creating goal:', error);
        throw error;
    }
};

/**
 * Update a goal
 * @param {string} goalId - The goal ID
 * @param {Object} updates - Fields to update
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} Updated goal object
 */
export const updateGoal = async (goalId, updates, userId) => {
    if (!userId || !goalId) {
        throw new Error('User ID and Goal ID are required');
    }

    try {
        // Get the goal to check permissions
        const goal = await getGoalById(goalId, userId);

        // Check permissions
        const hasPermission = await canEdit(goal.calendarId, userId);
        if (!hasPermission) {
            throw new Error('Insufficient permissions to update goal');
        }

        const goalRef = doc(db, GOALS_COLLECTION, goalId);

        const allowedUpdates = {
            ...(updates.title && { title: updates.title }),
            ...(updates.description !== undefined && { description: updates.description }),
            ...(updates.color && { color: updates.color }),
            ...(updates.status && { status: updates.status }),
            updatedAt: serverTimestamp()
        };

        await updateDoc(goalRef, allowedUpdates);

        return {
            ...goal,
            ...allowedUpdates,
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error updating goal:', error);
        throw error;
    }
};

/**
 * Delete a goal
 * @param {string} goalId - The goal ID
 * @param {string} userId - The user's ID
 * @returns {Promise<void>}
 */
export const deleteGoal = async (goalId, userId) => {
    if (!userId || !goalId) {
        throw new Error('User ID and Goal ID are required');
    }

    try {
        // Get the goal to check permissions
        const goal = await getGoalById(goalId, userId);

        // Check permissions
        const hasPermission = await canEdit(goal.calendarId, userId);
        if (!hasPermission) {
            throw new Error('Insufficient permissions to delete goal');
        }

        // Delete the goal
        const goalRef = doc(db, GOALS_COLLECTION, goalId);
        await deleteDoc(goalRef);

        // Optionally, we could also remove goalId from associated tasks
        // But keeping it allows for historical tracking
    } catch (error) {
        console.error('Error deleting goal:', error);
        throw error;
    }
};

/**
 * Calculate progress for a goal based on associated tasks
 * @param {string} goalId - The goal ID
 * @param {string} calendarId - The calendar ID (required for permissions)
 * @returns {Promise<Object>} Progress metrics
 */
export const getGoalProgress = async (goalId, calendarId) => {
    if (!goalId) {
        throw new Error('Goal ID is required');
    }

    if (!calendarId) {
        throw new Error('Calendar ID is required');
    }

    try {
        const tasksRef = collection(db, TASKS_COLLECTION);
        const q = query(
            tasksRef,
            where('calendarId', '==', calendarId),
            where('goalId', '==', goalId)
        );

        const querySnapshot = await getDocs(q);
        const tasks = [];

        querySnapshot.forEach((docSnap) => {
            tasks.push({
                id: docSnap.id,
                ...docSnap.data()
            });
        });

        // Calculate metrics
        const totalTasks = tasks.length;
        const completedTasks = tasks.filter(t => t.completed || t.status === 'completed').length;
        const inProgressTasks = tasks.filter(t => t.status === 'in-progress').length;
        const notStartedTasks = tasks.filter(t => t.status === 'not-started' || !t.status).length;
        const blockedTasks = tasks.filter(t => t.status === 'blocked').length;
        const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

        return {
            totalTasks,
            completedTasks,
            inProgressTasks,
            notStartedTasks,
            blockedTasks,
            completionPercentage
        };
    } catch (error) {
        console.error('Error calculating goal progress:', error);
        throw error;
    }
};
